/** Model for API exceptions **/
export interface ExceptionDetail extends Error {
  errorCode: string;
  errorStatus: string;
  errorMessage: string;
  retryable: boolean;
  /**
   * Downstream request id (from the `x-amzn-RequestId` response header). Gives
   * SDEs a direct handle to correlate with backend logs without re-deriving it.
   */
  requestId?: string;
  /**
   * Amazon service error type (from the `x-amzn-ErrorType` response header),
   * e.g. `AccessDeniedException`.
   */
  errorType?: string;
  /** Raw response body, when present, for the engineer-facing details panel. */
  responseBody?: string;
  /**
   * Transport-level error code set by Axios when there is no HTTP response,
   * e.g. `ERR_NETWORK` (connection/CORS/unreachable) or `ECONNABORTED` /
   * `ETIMEDOUT` (timeout). Used to classify status-less failures into a
   * connectivity message instead of the opaque generic bucket.
   */
  transportCode?: string;
}

/** Case-insensitive lookup of a response header from an Axios headers object. */
const getHeader = (headers: any, name: string): string | undefined => {
  if (!headers) {
    return undefined;
  }
  const lower = name.toLowerCase();
  const key = Object.keys(headers).find((k) => k.toLowerCase() === lower);
  return key ? headers[key] : undefined;
};

/** Best-effort stringify of a response body for display. */
const stringifyBody = (body: any): string | undefined => {
  if (body === undefined || body === null) {
    return undefined;
  }
  if (typeof body === 'string') {
    return body;
  }
  try {
    return JSON.stringify(body);
  } catch {
    return undefined;
  }
};

export interface BulkExceptionDetail {
  requestDetails: any;
  exceptionDetail: ExceptionDetail;
}

/**
 * Maps an Axios/unknown error into an ExceptionDetail without assuming the
 * shape of the error. Network errors, timeouts and CORS failures have no
 * `error.response`, so accessing it directly would throw a TypeError and mask
 * the real failure. All field accesses here are null-safe.
 */
export const toExceptionDetail = (error: any): ExceptionDetail =>
  ({
    errorCode: error?.response?.data?.errorCode,
    errorStatus: error?.response?.status,
    // Prefer the descriptive server-provided message (response body) over the
    // generic Axios message (e.g. "Request failed with status code 400").
    errorMessage: error?.response?.data?.message ?? error?.message,
    retryable: error?.retryable,
    requestId:
      getHeader(error?.response?.headers, 'x-amzn-RequestId') ??
      getHeader(error?.response?.headers, 'x-amz-request-id'),
    errorType: getHeader(error?.response?.headers, 'x-amzn-ErrorType'),
    responseBody: stringifyBody(error?.response?.data),
    transportCode: error?.code
  } as ExceptionDetail);
